import React, { useMemo } from "react";
import { useParams } from "react-router-dom";
import { Box, Card, Chip, CircularProgress, Grid, Typography } from "@mui/material";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import TitleBar from "components/TitleBar";
import { useGetHSEKpisQuery } from "store/slices/apis/hseDashboardSlice";
import { enterpriseTokens, overviewChartPalette } from "styles/theme";

/* ---------- helpers ---------- */
const toNumber = (val) => {
  if (val === null || val === undefined) return 0;
  if (typeof val === "number") return val;
  const cleaned = String(val).replace(/,/g, "");
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
};

const formatNumber = (num) => Number(num || 0).toLocaleString("en-IN");

const SafetyOverview = () => {
  const { projectId } = useParams();
  const { data, isError, isFetching: isLoading } = useGetHSEKpisQuery({ projectId });
  const safetyApiData = data?.data || {}
  const totalNum = safetyApiData?.totalManHours || 0;
  const safeNum = safetyApiData?.totalSafeManHours || 0;
  const unsafeNum = totalNum - safeNum;

  const nearMissValue = safetyApiData?.nearMissIncidents ?? 7
  const fatalitiesValue = safetyApiData?.fatalities || 0;

  const totalText = formatNumber(totalNum)
  const hasData = toNumber(totalNum) > 0;

  const safetyData = [
    {
      label: "Work Hours Logged",
      value: formatNumber(safetyApiData?.totalManHours || "0"),
      color: overviewChartPalette.indigo,
    },
    {
      label: "Safe Work Hours",
      value: formatNumber(safetyApiData?.totalSafeManHours || "0"),
      color: overviewChartPalette.teal,
    },
    {
      label: "Near-Miss Alerts",
      value: formatNumber(safetyApiData?.nearMissIncidents ?? 7),
      color: overviewChartPalette.amber,
    },
    {
      label: "Fatalities",
      value: formatNumber(safetyApiData?.fatalities ?? 0),
      color: overviewChartPalette.coral,
    },
  ];

  const options = useMemo(() => {
    const seriesData = [
      { name: "Safe Work Hours", y: safeNum, color: overviewChartPalette.teal },
      { name: "Exception Hours", y: unsafeNum, color: overviewChartPalette.amber },
    ];
    return {
      chart: {
        type: "pie",
        height: 220,
        backgroundColor: "transparent",
        events: {
          render: function () {
            const chart = this;

            if (chart.centerText) chart.centerText.destroy();
            if (chart.subText) chart.subText.destroy();

            const centerX = chart.plotLeft + chart.plotWidth / 2;
            const centerY = chart.plotTop + chart.plotHeight / 1.45;

            chart.centerText = chart.renderer
              .text(`${totalText}`, centerX, centerY - 5)
              .css({
                fontSize: "32px",
                fontWeight: 600,
                fontFamily: "Outfit, sans-serif",
                color: overviewChartPalette.navy,
                textAnchor: "middle",
              })
              .attr({ align: "center" })
              .add();

            chart.subText = chart.renderer
              .text("Work Hours Logged", centerX, centerY + 20)
              .css({
                fontSize: "12px",
                fontFamily: "Outfit, sans-serif",
                color: "#00000066",
                textAnchor: "middle",
              })
              .attr({ align: "center" })
              .add();
          },
        },
      },

      title: null,

      plotOptions: {
        pie: {
          startAngle: -120,
          endAngle: 120,
          innerSize: "75%",
          dataLabels: { enabled: false },
          borderWidth: 2,
          borderColor: "#fff",
          size: "150%",
          center: ["50%", "75%"],
        },
      },

      series: [{ data: seriesData }],

      tooltip: {
        formatter: function () {
          const point = this.point;
          const hrs = point.y ?? 0;
          const pct =
            totalNum > 0 ? ((hrs / totalNum) * 100).toFixed(2) : "0.00";
          return `<b>${point.name}</b><br/>${hrs.toLocaleString()} hrs<br/>${pct}% of total`;
        },
      },

      credits: { enabled: false },
    };
  }, [totalText, totalNum])

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: "3px",
        border: `1px solid ${enterpriseTokens.lineSoft}`,
        p: 0,
        height: "100%",
        pb: 2,
        boxSizing: "border-box",
        backgroundColor: "#FFFFFF",
      }}
    >
      <Grid container spacing={0}>
        <TitleBar
          title={"Safety"}
          description={"Tracks safety performance to reduce incidents and fatalities."}
        />
      </Grid>

      <Grid container spacing={3} sx={{ p: 2, pb: 0 }}>
        {isLoading && (
          <Grid size={12}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 6,
              }}
            >
              <CircularProgress />
            </Box>
          </Grid>
        )}


        {isError && (
          <Grid size={12}>
            <Typography color="error">
              Failed to load safety data.
            </Typography>
          </Grid>
        )}

        {!isLoading && !isError &&
          <>
            {/* LEFT: numbers (same style as you had) */}
            <Grid size={{ xs: 12, md: 5 }}>
              {!hasData ? (
                <Typography
                  sx={{
                    fontFamily: "Outfit, sans-serif",
                    fontSize: 14,
                    color: "#00000099",
                  }}
                >
                  No safety data available for this project.
                </Typography>
              ) : (
                <Box sx={{ display: "grid", gap: 1.5 }}>
                  {safetyData.map((item) => (
                    <Box
                      key={item.label}
                      sx={{
                        p: 1.5,
                        borderRadius: "3px",
                        border: `1px solid ${enterpriseTokens.lineSoft}`,
                        backgroundColor: "#F8FAFC",
                        borderLeft: `4px solid ${item.color}`,
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: "Inter, Outfit, sans-serif",
                          fontWeight: 800,
                          fontSize: "11px",
                          lineHeight: "14px",
                          letterSpacing: "0px",
                          color: enterpriseTokens.muted,
                          textTransform: "uppercase",
                        }}
                      >
                        {item.label}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: "Inter, Outfit, sans-serif",
                          fontWeight: 850,
                          fontSize: "26px",
                          lineHeight: "100%",
                          letterSpacing: "0px",
                          color: enterpriseTokens.navy,
                          mt: "5px",
                        }}
                      >
                        {item.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Grid>

            {/* RIGHT: Donut + incident chips */}
            <Grid size={{ xs: 12, md: 7 }}>
              {hasData ? (
                <Box sx={{ width: "100%", margin: "0 auto" }}>
                  <HighchartsReact highcharts={Highcharts} options={options} />

                  {/* ✅ Option A: Incidents shown as badges (not part of donut scale) */}
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      justifyContent: "center",
                      mt: 1.5,
                      flexWrap: "wrap",
                    }}
                  >
                    <Chip
                      label={`Near-Miss Alerts: ${nearMissValue}`}
                      sx={{
                        fontFamily: "Inter, Outfit, sans-serif",
                        bgcolor: "#FFF3ED",
                        color: "#E65F2B",
                        fontWeight: 800,
                        borderRadius: "999px",
                      }}
                    />
                    <Chip
                      label={`Fatalities: ${fatalitiesValue}`}
                      sx={{
                        fontFamily: "Inter, Outfit, sans-serif",
                        bgcolor: "#FFEDEF",
                        color: "#FF383C",
                        fontWeight: 800,
                        borderRadius: "999px",
                      }}
                    />
                  </Box>
                </Box>
              ) : (
                <Box sx={{ height: 220 }} />
              )}
            </Grid>
          </>
        }
      </Grid>
    </Card>
  );
};

export default SafetyOverview;
